import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { PlusCircle, TrendingDown, ReceiptText, Building2, FolderOpen } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { DataTable } from '@/components/table'
import DataTableSkeleton from '@/components/ui/data-table-skeleton'
import { PageHeader } from '@/components/layout/page-header'
import { Main } from '@/components/layout/main'
import { DeleteDialog } from '@/components/delete-dialog'
import { toast } from '@/hooks/use-toast'
import { handleServerError } from '@/utils/handle-server-error'
import {
  expensesService,
  type ExpenseStatus,
  type ExpenseType,
  type ExpenseCategory,
  type Expense,
  type CreateExpenseInput,
  type UpdateExpenseInput,
} from '@/api/expenses'
import { useExpensesColumns } from './components/expenses-columns'
import { ExpensesToolbar } from './components/expenses-toolbar'
import { ExpenseFormDialog } from './components/expense-form-dialog'
import { BillExpenseDialog } from './components/bill-expense-dialog'

export type ExpensesTableParams = {
  page: number
  page_size: number
  type?: ExpenseType
  category?: ExpenseCategory
  status?: ExpenseStatus
  ordering?: string | null
}

const fmtMoney = (v?: string) => {
  if (!v) return '0,00 MAD'
  const num = parseFloat(v)
  if (isNaN(num)) return v
  return new Intl.NumberFormat('fr-MA', {
    style: 'currency',
    currency: 'MAD',
  }).format(num)
}

const STATUS_TABS: { value: 'all' | ExpenseStatus; labelKey: string }[] = [
  { value: 'all', labelKey: 'exp_tab_all' },
  { value: 'a_refacturer', labelKey: 'exp_tab_a_refacturer' },
  { value: 'refacturee', labelKey: 'exp_tab_refacturee' },
  { value: 'non_facturable', labelKey: 'exp_tab_non_facturable' },
]

export function ExpenseList() {
  const { t } = useTranslation()
  const queryClient = useQueryClient()

  const [params, setParams] = useState<ExpensesTableParams>({
    page: 0,
    page_size: 20,
    ordering: '-expense_date',
  })
  const [activeTab, setActiveTab] = useState<'all' | ExpenseStatus>('all')

  const [isFormOpen, setIsFormOpen] = useState(false)
  const [editingExpense, setEditingExpense] = useState<Expense | null>(null)
  const [deletingExpense, setDeletingExpense] = useState<Expense | null>(null)
  const [isDeleteOpen, setIsDeleteOpen] = useState(false)
  const [billingExpense, setBillingExpense] = useState<Expense | null>(null)
  const [isBillOpen, setIsBillOpen] = useState(false)

  const apiParams = {
    limit: params.page_size,
    offset: params.page * params.page_size,
    type: params.type,
    category: params.category,
    status: activeTab !== 'all' ? activeTab : undefined,
    ordering: params.ordering ?? '-expense_date',
  }

  const { data, isLoading } = useQuery({
    queryKey: ['expenses', params, activeTab],
    queryFn: () => expensesService.list(apiParams),
  })

  const { data: summary } = useQuery({
    queryKey: ['expenses-summary'],
    queryFn: () => expensesService.summary(),
    staleTime: 60_000,
  })

  const pages = data ? Math.ceil(data.count / params.page_size) : 0

  const handleOpenCreate = () => {
    setEditingExpense(null)
    setIsFormOpen(true)
  }

  const handleOpenEdit = (expense: Expense) => {
    setEditingExpense(expense)
    setIsFormOpen(true)
  }

  const handleOpenDelete = (expense: Expense) => {
    setDeletingExpense(expense)
    setIsDeleteOpen(true)
  }

  const handleOpenBill = (expense: Expense) => {
    setBillingExpense(expense)
    setIsBillOpen(true)
  }

  const handleUnbill = async (expense: Expense) => {
    try {
      await expensesService.unbill(expense.id)
      queryClient.invalidateQueries({ queryKey: ['expenses'] })
      queryClient.invalidateQueries({ queryKey: ['expenses-summary'] })
      toast({ title: t('exp_unbill_success') })
    } catch (err) {
      handleServerError(err)
    }
  }

  const handleSaved = async (values: CreateExpenseInput) => {
    try {
      if (editingExpense) {
        const updateData: UpdateExpenseInput = {
          category: values.category,
          description: values.description,
          amount: values.amount,
          expense_date: values.expense_date,
          billable: values.billable,
          assigned_to: values.assigned_to,
        }
        await expensesService.update(editingExpense.id, updateData)
        toast({ title: t('exp_save_success') })
      } else {
        await expensesService.create(values)
        toast({ title: t('exp_save_success') })
      }
      queryClient.invalidateQueries({ queryKey: ['expenses'] })
      queryClient.invalidateQueries({ queryKey: ['expenses-summary'] })
      setIsFormOpen(false)
    } catch (err) {
      handleServerError(err)
      throw err
    }
  }

  const handleDelete = async () => {
    if (!deletingExpense) return
    await expensesService.remove(deletingExpense.id)
    queryClient.invalidateQueries({ queryKey: ['expenses'] })
    queryClient.invalidateQueries({ queryKey: ['expenses-summary'] })
    toast({ title: t('exp_delete_success') })
    setIsDeleteOpen(false)
    setDeletingExpense(null)
  }

  const columns = useExpensesColumns({
    onEdit: handleOpenEdit,
    onDelete: handleOpenDelete,
    onBill: handleOpenBill,
    onUnbill: handleUnbill,
  })

  const handleTabChange = (value: string) => {
    setActiveTab(value as 'all' | ExpenseStatus)
    setParams((p) => ({ ...p, page: 0 }))
  }

  return (
    <>
      <Main>
        <PageHeader
          leading={<TrendingDown className="h-6 w-6 text-primary" />}
          title={t('depenses')}
          description={t('depenses_desc')}
          actions={
            <Button onClick={handleOpenCreate}>
              <PlusCircle className="h-4 w-4" />
              {t('exp_create')}
            </Button>
          }
        />

        {/* KPI cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-muted-foreground mb-1 text-sm">
                <TrendingDown className="h-4 w-4" />
                {t('exp_kpi_total')}
              </div>
              <p className="text-xl font-bold">{fmtMoney(summary?.grand_total)}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-muted-foreground mb-1 text-sm">
                <ReceiptText className="h-4 w-4" />
                {t('exp_kpi_billed')}
              </div>
              <p className="text-xl font-bold">{fmtMoney(summary?.billed_total)}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-muted-foreground mb-1 text-sm">
                <Building2 className="h-4 w-4" />
                {t('exp_kpi_cabinet')}
              </div>
              <p className="text-xl font-bold">
                {fmtMoney(summary?.by_type?.cabinet ?? '0')}
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-muted-foreground mb-1 text-sm">
                <FolderOpen className="h-4 w-4" />
                {t('exp_kpi_dossier')}
              </div>
              <p className="text-xl font-bold">
                {fmtMoney(summary?.by_type?.dossier ?? '0')}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Status tabs */}
        <Tabs value={activeTab} onValueChange={handleTabChange} className="mb-4">
          <TabsList>
            {STATUS_TABS.map((tab) => (
              <TabsTrigger key={tab.value} value={tab.value}>
                {t(tab.labelKey)}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>

        {/* Table */}
        {isLoading ? (
          <DataTableSkeleton columns={7} rows={10} />
        ) : (
          <DataTable
            columns={columns}
            data={data?.results ?? []}
            pages={pages}
            params={params}
            setParams={setParams}
            ToolbarName={ExpensesToolbar}
            storageKey="expenses-table"
          />
        )}
      </Main>

      <ExpenseFormDialog
        open={isFormOpen}
        onOpenChange={setIsFormOpen}
        expense={editingExpense}
        onSaved={handleSaved}
      />

      <DeleteDialog
        open={isDeleteOpen}
        onOpenChange={(o) => setIsDeleteOpen(o)}
        getDeleteName={() =>
          deletingExpense ? deletingExpense.description : ''
        }
        onDeleted={handleDelete}
        deleteTitle={t('delete')}
        deleteRow={async () => {}}
      />

      <BillExpenseDialog
        open={isBillOpen}
        onOpenChange={setIsBillOpen}
        expense={billingExpense}
        onBilled={() => {
          queryClient.invalidateQueries({ queryKey: ['expenses'] })
          queryClient.invalidateQueries({ queryKey: ['expenses-summary'] })
        }}
      />
    </>
  )
}
